import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * BubblyContext — a tiny shared mechanism so any "Contact us / chat" entry point
 * (the Contact-us menu, the Guide page, etc.) can open the single app-wide
 * Bubbly assistant instead of spinning up a second chat panel.
 *
 * The BubblyWidget owns its own `open` state too (its launcher toggles it), but
 * subscribes to this context's `open` flag so external callers can pop it open.
 * We bump `requestId` on every `openBubbly()` so repeat clicks re-open even if
 * the widget was just closed.
 */
export interface BubblyOpenOptions {
  /** Open in the big centered window (e.g. from the Resources page). */
  expanded?: boolean;
}

interface BubblyContextValue {
  /** Latest open-request counter; the widget opens whenever this increments. */
  openRequestId: number;
  /** Whether the latest open request asked for the big window. */
  openRequestExpanded: boolean;
  /** Open the Bubbly assistant from anywhere in the app. */
  openBubbly: (opts?: BubblyOpenOptions) => void;
}

const BubblyContext = createContext<BubblyContextValue | null>(null);

export function BubblyProvider({ children }: { children: ReactNode }) {
  const [openRequestId, setOpenRequestId] = useState(0);
  const [openRequestExpanded, setOpenRequestExpanded] = useState(false);
  const openBubbly = useCallback((opts?: BubblyOpenOptions) => {
    setOpenRequestExpanded(!!opts?.expanded);
    setOpenRequestId((n) => n + 1);
  }, []);
  const value = useMemo(
    () => ({ openRequestId, openRequestExpanded, openBubbly }),
    [openRequestId, openRequestExpanded, openBubbly],
  );
  return <BubblyContext.Provider value={value}>{children}</BubblyContext.Provider>;
}

/**
 * Returns `openBubbly()`. Safe to call outside the provider (e.g. on /login):
 * it's a no-op there, so callers don't need to guard.
 */
export function useBubbly(): { openBubbly: (opts?: BubblyOpenOptions) => void } {
  const ctx = useContext(BubblyContext);
  return { openBubbly: ctx?.openBubbly ?? (() => {}) };
}

/** Internal — used by BubblyWidget to subscribe to external open requests. */
export function useBubblyOpenRequest(): { id: number; expanded: boolean } {
  const ctx = useContext(BubblyContext);
  return { id: ctx?.openRequestId ?? 0, expanded: ctx?.openRequestExpanded ?? false };
}
